<template>
  <button
    class="aha-btn"
    :class="[`aha-btn--${variant}`, {
      'aha-btn--block': block,
    }]"
    :type="type"
    :disabled="disabled"
    @click="$emit('click')"
  >
    <slot />
  </button>
</template>

<script>
export default {
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'light'].includes(value),
    },
    type: {
      type: String,
      default: 'button',
      validator: (value) => ['button', 'submit', 'reset'].includes(value),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style lang="scss" scoped>
.aha-btn {
  tab-size: 4;
  -webkit-text-size-adjust: 100%;
  --rem: 16;
  --animate-duration: 1s;
  --animate-delay: 1s;
  --animate-repeat: 1;
  --swiper-theme-color: #007aff;
  --swiper-navigation-size: 44px;
  border-width: 0;
  border-style: solid;
  border-color: currentColor;
  --tw-ring-inset: var(--tw-empty,/*!*/ /*!*/);
  --tw-ring-offset-width: 0px;
  --tw-ring-offset-color: #fff;
  --tw-ring-color: rgba(59, 130, 246, 0.5);
  --tw-ring-offset-shadow: 0 0 #0000;
  --tw-ring-shadow: 0 0 #0000;
  font-family: inherit;
  margin: 0;
  text-transform: none;
  background-image: none;
  cursor: pointer;
  -webkit-transform-style: preserve-3d;
  transform-style: preserve-3d;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  font-weight: 700;
  --tw-translate-x: 0;
  --tw-translate-y: 0;
  --tw-rotate: 0;
  --tw-skew-x: 0;
  --tw-skew-y: 0;
  --tw-scale-x: 1;
  --tw-scale-y: 1;
  transform: translateX(var(--tw-translate-x)) translateY(var(--tw-translate-y))
    rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y))
    scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 100ms;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  --tw-bg-opacity: 1;
  background-color: rgba(255, 255, 255, var(--tw-bg-opacity));
  --tw-text-opacity: 1;
  color: rgba(40, 46, 62, var(--tw-text-opacity));
  --tw-shadow: 0px 4px 0px #D3D7E1;
  box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
  padding: 11px 16px 11px 16px;
  font-size: 0.875rem;
  line-height: 1.25rem;
  min-width: 4rem;
  -webkit-appearance: button;
  appearance: button;
  box-sizing: border-box;
  position: relative;

  &:hover:not(:active):not(:disabled) {
    --tw-translate-y: 0.0625rem;
  }

  &:focus {
    outline: none !important;
  }

  &:active:not(:disabled) {
    --tw-translate-y: 0.25rem;
    --tw-shadow: 0 0 #0000;
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &--primary {
    --tw-bg-opacity: 1;
    background-color: rgba(52, 142, 246, var(--tw-bg-opacity));
    --tw-shadow: 0 4px 0px #257ADC;
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
    color: #FFFFFF;

    &:hover:not(:active):not(:disabled) {
      --tw-shadow: 0px 3px 0px #257ADC;
      box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
    }

    &:active:not(:disabled) {
      --tw-bg-opacity: 1;
      background-color: rgba(37, 122, 220, var(--tw-bg-opacity));
    }
  }

  &--secondary {
    --tw-bg-opacity: 1;
    background-color: rgba(239, 241, 247, var(--tw-bg-opacity));
    --tw-shadow: 0 4px 0px #DFE3E9;
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
    color: #282E3E;

    &:hover:not(:active):not(:disabled) {
      --tw-shadow: 0px 3px 0px #DFE3E9;
      box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
    }

    &:active:not(:disabled) {
      --tw-bg-opacity: 1;
      background-color: rgba(239, 241, 247, var(--tw-bg-opacity));
    }
  }

  &--light {
    --tw-bg-opacity: 1;
    background-color: rgba(255, 255, 255, var(--tw-bg-opacity));
    --tw-text-opacity: 1;
    color: rgba(40, 46, 62, var(--tw-text-opacity));
    --tw-shadow: 0px 4px 0px #D3D7E1;
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);

    &:hover:not(:active):not(:disabled) {
      --tw-shadow: 0px 3px 0px #D3D7E1;
      box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
    }

    &:active:not(:disabled) {
      --tw-bg-opacity: 1;
      background-color: rgba(211, 215, 225, var(--tw-bg-opacity));
    }
  }

  &--block {
    width: 100%;
  }
}
</style>
